import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Sparkles as SparkIcon, RefreshCw, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import DeleteAccountDialog from "@/components/DeleteAccountDialog";

function chipTone(status) {
  if (["paid", "captured", "SUCCESS"].includes(status))
    return "text-emerald-300 border-emerald-500/40 bg-emerald-500/5";
  if (["failed", "signature_invalid"].includes(status))
    return "text-rose-300 border-rose-500/40 bg-rose-500/5";
  if (["pending", "created", "initiated"].includes(status))
    return "text-amber-300 border-amber-500/40 bg-amber-500/5";
  return "text-slate-300 border-slate-700 bg-slate-900/40";
}

function Chip({ label, tone }) {
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-sm border font-mono text-[10px] uppercase tracking-[0.18em] ${tone}`}
    >
      {label}
    </span>
  );
}

export default function Account() {
  const { user, loading: authLoading } = useAuth();
  const [payments, setPayments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/payments/mine");
      setPayments(data);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  if (authLoading) {
    return (
      <div className="text-center py-16 text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-20" data-testid="account-gate">
        <SparkIcon className="h-8 w-8 text-cyan-400 mx-auto mb-3" />
        <h2 className="font-display text-2xl font-bold">Sign in to view payments</h2>
        <p className="text-sm text-slate-400 mt-2">Your credits and payment history live here.</p>
        <Link to="/" className="inline-block mt-6">
          <Button className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 rounded-sm font-semibold">
            Back to app
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="account-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400 mb-1">
            Billing
          </div>
          <h1 className="font-display text-3xl font-bold">My payments</h1>
          <p className="text-sm text-slate-400 mt-1">
            Signed in as <span className="text-slate-200">{user.email}</span>
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={load}
          disabled={busy}
          className="bg-transparent border-slate-700 text-slate-100 hover:bg-slate-900 rounded-sm gap-1.5"
          data-testid="account-refresh"
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          Refresh
        </Button>
      </div>

      {busy && payments.length === 0 ? (
        <div className="py-10 text-center text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mx-auto" />
        </div>
      ) : payments.length === 0 ? (
        <div
          className="border border-dashed border-slate-800 rounded-sm p-10 text-center"
          data-testid="no-payments"
        >
          <p className="text-sm text-slate-400">You haven't made any purchases yet.</p>
          <Link to="/" className="inline-block mt-4">
            <Button className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 rounded-sm font-semibold">
              View plans
            </Button>
          </Link>
        </div>
      ) : (
        <div className="bg-slate-900/60 border border-slate-800 rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 bg-slate-950/50">
                <tr>
                  <th className="text-left px-4 py-2">Pack</th>
                  <th className="text-left px-4 py-2">Amount</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Credits</th>
                  <th className="text-left px-4 py-2">Order ID</th>
                  <th className="text-left px-4 py-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr
                    key={p.order_id}
                    data-testid={`payment-row-${p.order_id}`}
                    className="border-t border-slate-800 hover:bg-slate-900/40"
                  >
                    <td className="px-4 py-2">{p.pack_name}</td>
                    <td className="px-4 py-2">
                      ₹{p.amount_display}
                      <span className="text-[10px] font-mono text-slate-500 ml-1">
                        {p.currency}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <Chip label={p.payment_status} tone={chipTone(p.payment_status)} />
                    </td>
                    <td className="px-4 py-2 text-cyan-300 font-mono">
                      {p.credited ? `+${p.credits}` : "—"}
                    </td>
                    <td className="px-4 py-2 text-[10px] font-mono text-slate-500">
                      {p.order_id}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-400">
                      {new Date(p.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div
        data-testid="danger-zone"
        className="mt-10 border border-rose-500/30 bg-rose-500/5 rounded-sm p-5"
      >
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-rose-400 mb-1">
          Danger zone
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-display text-base font-semibold text-slate-100">
              Delete my account
            </div>
            <div className="text-xs text-slate-400 mt-0.5">
              Permanently removes your account, projects, credit wallet and
              unpaid orders. Paid records are retained for financial audit.
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => setDeleteOpen(true)}
            data-testid="open-delete-account"
            className="bg-transparent border-rose-500/40 text-rose-300 hover:bg-rose-500/10 hover:text-rose-200 rounded-sm gap-1.5"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete account
          </Button>
        </div>
      </div>

      <DeleteAccountDialog open={deleteOpen} onOpenChange={setDeleteOpen} />
    </div>
  );
}
